import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { selectMaybeClient, selectSession } from '../model';
import { SessionType } from '@shared';
import { initializeIntercom, updateIntercomUser } from '../util';

function toUnixTimestamp(dateTime: Date | string | number | null | undefined): number {
  if (!dateTime) {
    return 0;
  }

  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
  const dateMs = date.getTime();
  if (Number.isNaN(dateMs)) {
    return 0;
  }

  return Math.floor(dateMs / 1000);
}

export function IntercomUpdate() {
  const { pathname } = useLocation();
  const session = useSelector(selectSession);
  const maybeClient = useSelector(selectMaybeClient);
  const clientAdlCount = maybeClient?.intakeSurvey?.clientAdlReceivedAssistanceCount;
  const clientPartOfGroupPlan =
    maybeClient?.intakeSurvey?.customHiddenFields.customGroupPlan === 'true';

  useEffect(() => {
    const advisor = session?.advisor;
    const sessionType = session?.sessionType;

    if (
      advisor &&
      session.intercomIdentityHash &&
      (sessionType === SessionType.advisor || sessionType === SessionType.wizard)
    ) {
      const isWizardImpersonating =
        sessionType === SessionType.wizard && session.impersonation?.isImpersonating;

      const firstName = isWizardImpersonating
        ? session.impersonation?.wizardAdvisorFirstName || ''
        : advisor.advisorFirstName || '';
      const lastName = isWizardImpersonating
        ? session.impersonation?.wizardAdvisorLastName || ''
        : advisor.advisorLastName || '';
      const name = `${firstName} ${lastName}`.trim();
      const advisorId = isWizardImpersonating
        ? session.impersonation?.wizardAdvisorId
        : advisor.advisorId;
      const advisorEmail = isWizardImpersonating
        ? session.impersonation?.wizardEmail
        : advisor.advisorEmail;
      const createdAtRaw = isWizardImpersonating
        ? session.impersonation?.wizardCreatedAtUnix ?? 0
        : toUnixTimestamp(advisor.advisorCreatedDateTime);
      const createdAt = Number.isFinite(createdAtRaw) ? createdAtRaw : 0;

      if (advisorId && advisorEmail) {
        updateIntercomUser(
          {
            name: name || '',
            email: advisorEmail,
            createdAt,
            userId: advisorId,
            userHash: session.intercomIdentityHash,
            clientAdlCount,
            clientPartOfGroupPlan,
            userType: session.intercomUserType,
          },
          pathname,
        );
      }
    } else if (
      sessionType === SessionType.client &&
      session.auth0Session?.authUserId &&
      session.intercomIdentityHash
    ) {
      const authUserId = session.auth0Session.authUserId;
      const clientUser = session.auth0Session.clientUser;
      const name = maybeClient
        ? `${maybeClient.clientFirstName} ${maybeClient.clientLastName}`.trim()
        : 'Client';
      const email = maybeClient?.clientEmail ?? clientUser?.email ?? '';
      const createdAt = maybeClient ? toUnixTimestamp(maybeClient.clientAddedDateTime) : 0;

      updateIntercomUser(
        {
          name,
          email,
          createdAt,
          userId: authUserId,
          userHash: session.intercomIdentityHash,
          clientAdlCount,
          userType: session.intercomUserType,
        },
        pathname,
      );
    }
  }, [pathname, session, maybeClient, clientAdlCount, clientPartOfGroupPlan]);

  useEffect(() => {
    initializeIntercom();
  }, []);
  return null;
}

export default IntercomUpdate;
